import React from 'react';
import PropTypes from 'prop-types';
const loaderVariants = {
  "page": "[&_.app-loader\\_\\_spinner]:tw-w-[56px] [&_.app-loader\\_\\_spinner]:tw-h-[56px] [&_.app-loader\\_\\_spinner]:tw-border-[5px] app-loader--page",
  "section": "app-loader--section",
  "tab": "app-loader--tab"
};



/**
 * Single dynamic loader used across the entire application.
 *
 * Variants:
 *  - "page"     : full route/page area loader (tall, centered spinner)
 *  - "section"  : default content/section loader
 *  - "tab"      : loader for the content area of a tab (keeps tabs/header visible)
 *  - "overlay"  : absolute overlay over its positioned parent (blocking actions)
 *  - "inline"   : small inline spinner (buttons, pagination, load-more)
 *  - "table"    : skeleton rows for tables (use `rows` / `columns`)
 *  - "card"     : skeleton cards for grids (use `cards`)
 *  - "skeleton" : generic skeleton lines (use `lines`)
 *
 * Backward compatible with the previous loaders:
 *  - <Loader /> renders a centered spinner with caption.
 *  - Legacy props `color`, `size`, `caption`, `containerHeight`, `minLoadTime` are supported.
 */
function Loader({
  variant = 'section',
  caption,
  size,
  color,
  containerHeight,
  // eslint-disable-next-line no-unused-vars
  minLoadTime,
  rows = 4,
  columns = 4,
  cards = 4,
  lines = 3,
  fullPage = false,
  className = '',
}) {
  const spinner = (
    <span
      className="tw-w-[44px] tw-h-[44px] tw-border-[4px] tw-border-solid tw-border-[var(--ui-primary,_#0d6efd)] tw-border-b-[transparent] tw-rounded-[50%] tw-inline-block tw-box-border tw-animate-[app-loader-rotate_0.8s_linear_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-loader__spinner"
      style={{
        ...(size ? { width: `${size}px`, height: `${size}px` } : null),
        ...(color ? { borderColor: color, borderBottomColor: 'transparent' } : null),
      }}
      role="status"
      aria-label="Loading"
    />
  );

  const captionNode = caption ? (
    <div className="tw-text-[var(--ui-secondary,_#6c757d)] tw-text-[0.9375rem] tw-font-medium app-loader__caption" style={color ? { color } : undefined}>
      {caption}
    </div>
  ) : null;

  // ---- Skeleton: table ----
  if (variant === 'table') {
    return (
      <div className={`tw-items-center tw-justify-center tw-w-full tw-block app-loader app-loader--table   ${className}`.trim()} aria-busy="true" role="status" aria-label={caption || "Loading"}>
        <div className="tw-w-full tw-flex tw-flex-col tw-gap-[14px] tw-p-[12px_4px] app-skel-table">
          {Array.from({ length: rows }).map((_, rIdx) => (
            <div className="tw-flex tw-gap-[16px] app-skel-row" key={rIdx}>
              {Array.from({ length: columns }).map((__, cIdx) => (
                <span className="[flex:1] tw-h-[18px] tw-block tw-rounded-[6px] [background:linear-gradient(_90deg,_rgba(0,_0,_0,_0.06)_25%,_rgba(0,_0,_0,_0.12)_37%,_rgba(0,_0,_0,_0.06)_63%_)] tw-bg-[400%_100%] tw-animate-[app-skel-shimmer_1.4s_ease_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-skel app-skel-cell" key={cIdx} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- Skeleton: cards ----
  if (variant === 'card') {
    return (
      <div className={`tw-items-center tw-justify-center tw-w-full tw-block app-loader app-loader--cards   ${className}`.trim()} aria-busy="true" role="status" aria-label={caption || "Loading"}>
        <div className="tw-grid tw-grid-cols-[repeat(auto-fill,_minmax(240px,_1fr))] tw-gap-[20px] tw-p-[8px_4px] tw-w-full app-skel-cards">
          {Array.from({ length: cards }).map((_, idx) => (
            <div className="tw-flex tw-flex-col tw-gap-[12px] tw-p-[16px] tw-border-[1px] tw-border-solid tw-border-[rgba(0,_0,_0,_0.06)] tw-rounded-[12px] tw-bg-[#fff] app-skel-card" key={idx}>
              <span className="tw-w-full tw-h-[140px] tw-block tw-rounded-[6px] [background:linear-gradient(_90deg,_rgba(0,_0,_0,_0.06)_25%,_rgba(0,_0,_0,_0.12)_37%,_rgba(0,_0,_0,_0.06)_63%_)] tw-bg-[400%_100%] tw-animate-[app-skel-shimmer_1.4s_ease_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-skel app-skel-thumb" />
              <span className="tw-h-[14px] tw-w-full tw-block tw-rounded-[6px] [background:linear-gradient(_90deg,_rgba(0,_0,_0,_0.06)_25%,_rgba(0,_0,_0,_0.12)_37%,_rgba(0,_0,_0,_0.06)_63%_)] tw-bg-[400%_100%] tw-animate-[app-skel-shimmer_1.4s_ease_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-skel app-skel-line app-skel-line--lg" />
              <span className="tw-h-[14px] tw-w-[75%] tw-block tw-rounded-[6px] [background:linear-gradient(_90deg,_rgba(0,_0,_0,_0.06)_25%,_rgba(0,_0,_0,_0.12)_37%,_rgba(0,_0,_0,_0.06)_63%_)] tw-bg-[400%_100%] tw-animate-[app-skel-shimmer_1.4s_ease_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-skel app-skel-line app-skel-line--md" />
              <span className="tw-h-[14px] tw-w-[45%] tw-block tw-rounded-[6px] [background:linear-gradient(_90deg,_rgba(0,_0,_0,_0.06)_25%,_rgba(0,_0,_0,_0.12)_37%,_rgba(0,_0,_0,_0.06)_63%_)] tw-bg-[400%_100%] tw-animate-[app-skel-shimmer_1.4s_ease_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-skel app-skel-line app-skel-line--sm" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- Skeleton: generic lines ----
  if (variant === 'skeleton') {
    return (
      <div className={`tw-flex tw-justify-center tw-w-full tw-flex-col tw-items-stretch tw-gap-[12px] tw-p-[8px_4px] app-loader app-loader--skeleton   ${className}`.trim()} aria-busy="true" role="status" aria-label={caption || "Loading"}>
        {Array.from({ length: lines }).map((_, idx) => (
          <span
            className={`tw-h-[14px] tw-block tw-rounded-[6px] [background:linear-gradient(_90deg,_rgba(0,_0,_0,_0.06)_25%,_rgba(0,_0,_0,_0.12)_37%,_rgba(0,_0,_0,_0.06)_63%_)] tw-bg-[400%_100%] tw-animate-[app-skel-shimmer_1.4s_ease_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-skel app-skel-line   ${idx === lines - 1 ? "tw-w-[45%] app-skel-line--sm" : "tw-w-full app-skel-line--lg"}`}
            key={idx}
          />
        ))}
      </div>
    );
  }

  // ---- Overlay (blocking actions over a positioned parent or full page) ----
  if (variant === 'overlay') {
    return (
      <div
        className={`tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center [background:rgba(255,_255,_255,_0.7)] [backdrop-filter:blur(1px)] tw-z-[1050] app-loader-overlay   ${fullPage ? "tw-fixed app-loader-overlay--fixed" : ""}      ${className}`.trim()}
        aria-busy="true"
      >
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[12px] tw-text-center app-loader__content">
          {spinner}
          {captionNode}
        </div>
      </div>
    );
  }

  // ---- Inline (small spinner, no min-height) ----
  if (variant === 'inline') {
    return (
      <span className={`tw-justify-center tw-inline-flex tw-items-center tw-gap-[8px] tw-w-auto app-loader app-loader--inline   ${className}`.trim()} aria-busy="true" role="status" aria-label={caption || "Loading"}>
        <span className="tw-w-[44px] tw-h-[44px] tw-border-[4px] tw-border-solid tw-border-[var(--ui-primary,_#0d6efd)] tw-border-b-[transparent] tw-rounded-[50%] tw-inline-block tw-box-border tw-animate-[app-loader-rotate_0.8s_linear_infinite] [@media(prefers-reduced-motion:_reduce)]:[animation-duration:2.4s] app-loader__spinner app-loader__spinner--sm" role="status" aria-label="Loading" />
        {caption ? <span className="tw-text-[var(--ui-secondary,_#6c757d)] tw-font-medium tw-text-[0.8125rem] app-loader__caption app-loader__caption--inline">{caption}</span> : null}
      </span>
    );
  }

  // ---- Spinner variants: page / section / tab (default) ----
  const minHeight =
    containerHeight ||
    (variant === 'page' ? '60vh' : variant === 'tab' ? '280px' : '300px');

  return (
    <div
      className={`tw-flex tw-items-center tw-justify-center tw-w-full app-loader ${loaderVariants[variant] || ""}      ${className}`.trim()}
      style={{ minHeight }}
      aria-busy="true"
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[12px] tw-text-center app-loader__content">
        {spinner}
        {captionNode}
      </div>
    </div>
  );
}

Loader.propTypes = {
  variant: PropTypes.oneOf([
    'page',
    'section',
    'tab',
    'overlay',
    'inline',
    'table',
    'card',
    'skeleton',
  ]),
  caption: PropTypes.string,
  size: PropTypes.number,
  color: PropTypes.string,
  containerHeight: PropTypes.string,
  minLoadTime: PropTypes.number,
  rows: PropTypes.number,
  columns: PropTypes.number,
  cards: PropTypes.number,
  lines: PropTypes.number,
  fullPage: PropTypes.bool,
  className: PropTypes.string,
};

export default Loader;
